import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Heading, Input, Select, Text } from '../../components/ui'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../../constants'
import { processN8nLogsService } from '../../services/processN8nLogs'
import type { ProcessN8nLog } from '../../types/processN8nLogs'

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('pt-BR')
}

function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return '—'
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const diff = end - start
  if (!Number.isFinite(diff) || diff < 0) return '—'
  const totalSeconds = Math.floor(diff / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function statusClass(status: string): string {
  const normalized = status.toLowerCase()
  if (normalized === 'success' || normalized === 'ok') return 'text-green-600 dark:text-green-400'
  if (normalized === 'error' || normalized === 'failed' || normalized === 'crashed') {
    return 'text-red-600 dark:text-red-400'
  }
  if (normalized === 'running' || normalized === 'waiting') {
    return 'text-amber-600 dark:text-amber-400'
  }
  return 'text-gray-700 dark:text-gray-300'
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

function getTodayDateInputValue(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
  }).format(new Date())
}

const ITEMS_PER_PAGE = 20
const DEFAULT_STATUS_OPTIONS = ['success', 'ok', 'error', 'failed', 'crashed', 'running', 'waiting']

type AgendamentosN8nLogTabProps = {
  onAppliedDateRangeChange?: (filters: { dateFrom?: string; dateTo?: string }) => void
}

const AgendamentosN8nLogTab: React.FC<AgendamentosN8nLogTabProps> = ({ onAppliedDateRangeChange }) => {
  const [rows, setRows] = useState<ProcessN8nLog[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('all')
  const [nome, setNome] = useState('')
  const [dateFrom, setDateFrom] = useState(() => getTodayDateInputValue())
  const [dateTo, setDateTo] = useState(() => getTodayDateInputValue())
  const [appliedStatus, setAppliedStatus] = useState('all')
  const [appliedNome, setAppliedNome] = useState('')
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => getTodayDateInputValue())
  const [appliedDateTo, setAppliedDateTo] = useState(() => getTodayDateInputValue())
  const [currentPage, setCurrentPage] = useState(1)
  const [searchVersion, setSearchVersion] = useState(0)
  const [selected, setSelected] = useState<ProcessN8nLog | null>(null)

  const loadLogs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await processN8nLogsService.list({
        status: appliedStatus,
        nome: appliedNome.trim() || undefined,
        dateFrom: appliedDateFrom || undefined,
        dateTo: appliedDateTo || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      })
      setRows(result.logs)
      setTotalRows(result.total)
    } catch (e) {
      setRows([])
      setTotalRows(0)
      setError(errorMessage(e, 'Erro ao carregar logs n8n'))
    } finally {
      setLoading(false)
    }
  }, [appliedStatus, appliedNome, appliedDateFrom, appliedDateTo, currentPage])

  useEffect(() => {
    void loadLogs()
  }, [loadLogs, searchVersion])

  const statusOptions = useMemo(() => {
    const unique = Array.from(
      new Set([...DEFAULT_STATUS_OPTIONS, ...rows.map((row) => row.status).filter(Boolean)])
    ).sort()
    return unique
  }, [rows])

  const totalPages = Math.max(1, Math.ceil(totalRows / ITEMS_PER_PAGE))
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE
  const pageEnd = pageStart + ITEMS_PER_PAGE
  const pagedRows = rows

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const clearFilters = () => {
    setStatus('all')
    setNome('')
    setDateFrom(getTodayDateInputValue())
    setDateTo(getTodayDateInputValue())
  }

  const handleSearch = () => {
    setAppliedStatus(status)
    setAppliedNome(nome)
    setAppliedDateFrom(dateFrom)
    setAppliedDateTo(dateTo)
    onAppliedDateRangeChange?.({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    })
    setCurrentPage(1)
    setSearchVersion((version) => version + 1)
  }

  return (
    <div className="space-y-4">
      <div className="px-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">Todos</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col min-w-[12rem]">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Nome</label>
            <Input
              value={nome}
                onChange={(e) => setNome(e.target.value)}
              placeholder="Filtrar por nome..."
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data inicial</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data final</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="ml-auto flex gap-2">
            <Button
              onClick={handleSearch}
              className="text-white"
              style={{ backgroundColor: BRAND_COLOR }}
              disabled={loading}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading}>
              Limpar filtros
            </Button>
          </div>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {loading ? 'Carregando...' : `${totalRows} execuções n8n`}
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{error}</Text>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Execution ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Início
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Fim
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tempo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {!loading && pagedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Nenhum log n8n encontrado.
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => setSelected(row)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {row.nome || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100">
                      {row.execution_id}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${statusClass(row.status)}`}>
                      {row.status || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatDateTime(row.started_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatDateTime(row.finished_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatDuration(row.started_at, row.finished_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  Mostrando {totalRows === 0 ? 0 : pageStart + 1} a{' '}
                  {Math.min(pageEnd, totalRows)} de {totalRows} execuções
                </Text>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber: number
                      if (totalPages <= 5) pageNumber = i + 1
                      else if (currentPage <= 3) pageNumber = i + 1
                      else if (currentPage >= totalPages - 2) pageNumber = totalPages - 4 + i
                      else pageNumber = currentPage - 2 + i
                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNumber ? 'bg-primary-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                        >
                          {pageNumber}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">
                Detalhes da execução n8n
              </Heading>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">ID:</span>{' '}
                <span className="font-mono break-all">{selected.id}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Nome:</span>{' '}
                <span>{selected.nome || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Execution ID:</span>{' '}
                <span className="font-mono break-all">{selected.execution_id}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Workflow ID:</span>{' '}
                <span className="font-mono break-all">{selected.workflow_id}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Status:</span>{' '}
                <span className={statusClass(selected.status)}>{selected.status || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Finalizado:</span>{' '}
                <span>{selected.finished ? 'Sim' : 'Não'}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Mode:</span>{' '}
                <span>{selected.mode || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Início:</span>{' '}
                <span>{formatDateTime(selected.started_at)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Fim:</span>{' '}
                <span>{formatDateTime(selected.finished_at)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Tempo:</span>{' '}
                <span>{formatDuration(selected.started_at, selected.finished_at)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Criado em:</span>{' '}
                <span>{formatDateTime(selected.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgendamentosN8nLogTab
